import {
  HotelOffers,
  User,
  Order,
  FPError,
  HotelSearchCriteria,
  Cars,
  ThreeDsTypes,
  isRight,
  Failure,
  Either,
  isLeft,
  failureKeys,
  orderFailureKeys,
} from 'types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { OrderService } from 'api';
import { Events } from './../../analytics/events';
import { changeHostToCurrent, mapError } from '../../utils';
import i18n from '../../plugins/i18n';
import { Expedia } from '../../3ds/expedia';
import { ThreeDsController } from '../../3ds/3ds';
import { SonicSdk } from '../../mc-sonic/sonicSdk';
import { OfferUseCase } from '@/use-case';

const threeDsController = new ThreeDsController();

let challengeEither: Promise<
  Either<Failure.Absract, ThreeDsTypes.InitSessionResponse>
> = null;

export class State {
  payment: User.Card = null;
  successConfirm: boolean = false;
  bookingLoading: boolean = false;
  failureDialog: Order.FailureDialog = new Order.FailureDialog();
  confirmDialog: Order.ConfirmDialog = new Order.ConfirmDialog();
  loadingDialog: Order.LoadingDialog = new Order.LoadingDialog();
  useCredits: boolean = false;
  order: Order.Ml.Request.Body = null;
  specialRequest: string = '';
  arrivalTime: string = '';
  loyaltyProgram: string = '';
  orderDetails: Order.OrderDetails = null;
  bookingStartTime: number = 0;
  isEPSOpen: boolean = false;
  threeDSState: ThreeDsTypes.FrameState = {
    show: false,
    loading: false,
  };
  pendingOrder: Order.PendingOrder = new Order.PendingOrder();
}

const getters = <GetterTree<State, any>>{
  getCheckSumHeaderSubtitle(state: State) {
    return state.payment
      ? String(
          i18n.global.t('hotelpage.payment.paymentCard', {
            card_type: state.payment.card_type,
            ending: state.payment.card_mask.slice(-4),
          }),
        )
      : '';
  },
};

const mutations = <MutationTree<State>>{
  setPayment(state: State, value: User.Card) {
    state.payment = null;
    state.payment = value;
  },

  showCVV(state: State, show: boolean) {
    state.confirmDialog.error = '';
    state.confirmDialog.show = show;
  },

  setCVV(state: State, cvv: string) {
    state.confirmDialog.cvv = cvv;
  },

  setLoading(state: State, loading: boolean) {
    state.bookingLoading = loading;
  },

  showSuccess(state: State, show: boolean) {
    state.confirmDialog.show = false;
    state.useCredits = false;
    state.successConfirm = show;
  },

  setFailure(state: State, error: FPError) {
    state.confirmDialog.show = false;
    state.failureDialog.message = error.message;
    state.failureDialog.error = 'Oops!';
    state.failureDialog.show = error ? true : false;
  },

  showFailure(state: State, show: boolean) {
    state.failureDialog.show = show;
  },

  setErrorOnCVVCard(state: State, error: string) {
    state.confirmDialog.error = error;
  },

  showLoadingDialog(
    state: State,
    {
      show,
      message,
      title,
    }: { show: boolean; message?: string; title?: string },
  ) {
    state.loadingDialog.show = show;
    state.loadingDialog.message = message || '';
    state.loadingDialog.title = title || '';
  },

  resetStore(state: State) {
    state.payment = null;
    state.successConfirm = false;
    state.bookingLoading = false;
    state.failureDialog = new Order.FailureDialog();
    state.confirmDialog = new Order.ConfirmDialog();
    state.loadingDialog = new Order.LoadingDialog();
    state.useCredits = false;
  },

  toggleCredits(state: State) {
    state.useCredits = !state.useCredits;
  },

  setSpecialRequest(state: State, request: string) {
    state.specialRequest = request;

    const persistence = {
      specialRequest: request,
    };

    User.PersistenceGuestData.save(persistence);
  },

  setLoyaltyProgram(state: State, program: string) {
    state.loyaltyProgram = program;
  },

  setArrivalTime(state: State, arrivalTime: string) {
    state.arrivalTime = arrivalTime;

    if (arrivalTime && arrivalTime.length > 0) {
      const persistence = {
        arrivalTime: arrivalTime,
      };

      User.PersistenceGuestData.save(persistence);
    }
  },

  setOrder(state: State, order: Order.Ml.Request.Body) {
    state.order = order;
  },

  setOrderId(state: State, order: Order.OrderDetails) {
    state.orderDetails = order;
  },

  noteBookingTime(state: State) {
    state.bookingStartTime = new Date().getTime();
  },

  showEPSIframe(state: State, show: boolean) {
    state.isEPSOpen = show;
    if (show) {
      Events.Hotel.Booking.ThreeDS.start();
    } else {
      Events.Hotel.Booking.ThreeDS.finish();
    }
  },

  setThreeDSState(
    state: State,
    threeDSState: Partial<ThreeDsTypes.FrameState>,
  ) {
    if (threeDSState.show) {
      Events.Hotel.Booking.ThreeDS.start();
    } else if (threeDSState.show === false && state.threeDSState.show) {
      Events.Hotel.Booking.ThreeDS.finish();

      state.threeDSState.source = null;
      threeDsController.cancel();
    }

    state.threeDSState = {
      ...state.threeDSState,
      ...threeDSState,
    };
  },

  showPendingOrder(
    state: State,
    {
      order,
      source,
    }: { order: Order.OrderItem; source: Order.PendingOrderSource },
  ) {
    if (order) {
      state.pendingOrder.order = order;
      state.pendingOrder.source = source;
      state.pendingOrder.show = true;
    }
  },

  removePendingOrder(state: State) {
    state.pendingOrder = new Order.PendingOrder();
  },

  showDeferredOrderCancellationStates(
    state: State,
    {
      loading,
      show,
      error,
    }: { show?: boolean; loading?: boolean; error?: string },
  ) {
    state.pendingOrder.cancellation.show = show;
    state.pendingOrder.cancellation.loading = loading;
    state.pendingOrder.cancellation.error = error;
  },
};

const actions = <ActionTree<State, any>>{
  selectCard(store: ActionContext<State, any>, card: User.Card) {
    store.commit('setPayment', card);
  },

  async book(store: ActionContext<State, any>, book: Book) {
    if (book.product !== Order.OrderProduct.hotel) {
      return;
    }

    if (store.state.confirmDialog.cvv.length < 3) {
      store.commit(
        'setErrorOnCVVCard',
        String(i18n.global.t('errors.booking.cvvRequired')),
      );
      return;
    }

    store.dispatch('support/refreshBookingId', {}, { root: true });

    const hotelOffer = book.offer as HotelOffers.Offer;

    const userV2 = store.rootState.user.userV2 as User.UserV2;

    let phone = book.contacts.phone;

    phone = phone.replace(/ /g, '');
    phone = phone.replace(/[^0-9]/g, '');
    phone = '+' + phone;

    let billingAddress = {
      address: 'Rua de São Bento',
      city: 'Lisbon',
      country: 'PT',
      postCode: '1200-109',
    };

    if (
      userV2.address.address &&
      userV2.address.city &&
      userV2.address.countryCode &&
      userV2.address.postCode
    ) {
      billingAddress = {
        address: userV2.address.address,
        city: userV2.address.city,
        country: userV2.address.countryCode,
        postCode: userV2.address.postCode,
      };
    }

    const booker: Order.Ml.Request.Booker = {
      firstName: userV2?.firstName, // traveler?
      lastName: userV2?.lastName, // traveler?
      email: book.contacts.email,
      phone: phone,
      billingAddress: billingAddress,
    };

    const guest = {
      name: book.guest.traveller,
      email: book.contacts.email,
    };

    const arrivatTimeInt = store.state.arrivalTime
      ? parseInt(store.state.arrivalTime.substring(0, 2))
      : null;

    const remarks = {
      specialRequest: store.state.specialRequest,
    };

    if (arrivatTimeInt) {
      remarks['arrivalTime'] = arrivatTimeInt;
    }

    const orderRequestBody: Order.Ml.Request.Body = {
      offerToken: hotelOffer.id,
      useCredits: store.state.useCredits,
      booker: booker,
      guests: [guest],
      remarks: remarks,
    };

    const selectedPayment = store.state.payment;

    if (!selectedPayment) {
      store.commit('setErrorOnCVVCard', 'Payment card is not selected');
      return;
    }

    if (selectedPayment.temporary) {
      const tmp = selectedPayment.temporary;

      const year =
        String(selectedPayment.exp_year).length === 2
          ? `20${selectedPayment.exp_year}`
          : selectedPayment.exp_year;

      const details: Order.Ml.Request.CardDetails = {
        number: tmp.number.value,
        cardHolder: tmp.cardholder.value,
        year,
        month: selectedPayment.exp_month,
        cvc: store.state.confirmDialog.cvv,
      };

      orderRequestBody.cardDetails = details;
    } else {
      const cardInfo = {
        cardId: selectedPayment.card_id,
        cvc: store.state.confirmDialog.cvv,
      };

      orderRequestBody.cardInfo = cardInfo;
    }

    Events.Hotel.Booking.startCvvVerification();

    let threeDsMeta: ThreeDsTypes.Meta = null;

    if (
      hotelOffer.orderFlow === HotelOffers.OrderFlow.expediaPayAtProperty2fa
    ) {
      const setupConnectorResult = await Expedia.setup();

      if (isRight(setupConnectorResult)) {
        threeDsMeta = setupConnectorResult.value;
      } else {
        store.commit('setErrorOnCVVCard', 'Failed to setup 2FA');
        return;
      }
    }

    if (threeDsMeta) {
      const expedia = {
        expedia: {
          version: threeDsMeta.version,
          encodedBrowserMetadata: threeDsMeta.encodedBrowserMetadata,
        },
      };
      orderRequestBody.metadata = expedia;
    }

    orderRequestBody.metadata = {
      ...orderRequestBody.metadata,
      processedByServer: true,
    };

    store.commit('setOrder', orderRequestBody);
    store.commit('setErrorOnCVVCard', '');
    store.commit('setLoading', true);

    if (store.state.confirmDialog.cvv.length < 3) {
      store.dispatch('onBookingFailed', {
        header: failureKeys.BAD_REQUEST,
        message: String(i18n.global.t('errors.booking.BAD_REQUEST')),
      });

      return;
    }
    store.commit('noteBookingTime');

    // Events.Hotel.Booking.start({
    //   hotel,
    //   criteria,
    //   offer: book.offer as HotelOffers.Offer,
    // });

    OrderService.createOrder(orderRequestBody).subscribe(
      res => {
        store.dispatch('proccessBooking', res);
      },
      err => {
        store.dispatch('onBookingFailed', err);
      },
    );
  },

  async proccessBooking(
    store: ActionContext<State, any>,
    orderDetails: Order.Ml.Response.Booking,
  ) {
    const metadata = orderDetails.metadata;

    if (metadata?.serverBookingFlow) {
      const hasDecta3DS =
        metadata && metadata.expedia && metadata.expedia.decta3DS;
      const hasBooking3DS =
        metadata && metadata.booking && metadata.booking.pci3DS;
      const hasItravex3DS =
        metadata && metadata.itravex && metadata.itravex.decta3DS;

      if (hasDecta3DS || hasBooking3DS || hasItravex3DS) {
        await store.dispatch('handleServerBookingFlowWith3DS', orderDetails);
      } else {
        store.dispatch('handleServerBookingFlow', orderDetails);
      }
      return;
    }
    if (
      metadata &&
      metadata.expedia &&
      metadata.expedia.paymentSessionId &&
      metadata.expedia.encodedInitConfig &&
      metadata.expedia.encodedChallengeConfig
    ) {
      const paymentSession: ThreeDsTypes.PaymentSession = {
        paymentSessionId: metadata.expedia.paymentSessionId,
        encodedConfig: metadata.expedia.encodedInitConfig,
      };

      const sessionEither = await Expedia.initSession(paymentSession);

      if (
        isLeft(sessionEither) ||
        (isRight(sessionEither) &&
          sessionEither.value.statusCode !== ThreeDsTypes.SessionStatus.Success)
      ) {
        store.dispatch('onBookingFailed', {
          header: failureKeys.TWO_FA_FAILED,
          message: 'Failed to create a 2FA session',
        });

        return;
      }
    }

    const hasDecta3DS =
      metadata && metadata.expedia && metadata.expedia.decta3DS;
    const hasBooking3DS =
      metadata && metadata.booking && metadata.booking.pci3DS;
    const hasItravex3DS =
      metadata && metadata.itravex && metadata.itravex.decta3DS;

    if (hasDecta3DS || hasBooking3DS || hasItravex3DS) {
      const iframeUrl = () => {
        if (hasDecta3DS) {
          return metadata.expedia.decta3DS;
        }

        if (hasItravex3DS) {
          return metadata.itravex.decta3DS;
        }

        return metadata.booking.pci3DS;
      };

      store.commit('showCVV', false);
      store.commit('setLoading', false);

      store.dispatch('showThreeDSIFrame', iframeUrl());

      const either = await threeDsController.subscribe();

      if (either.value === Failure.Terminated) {
      }

      store.commit('setThreeDSState', {
        show: false,
        loading: false,
        source: null,
      });

      store.commit('setLoading', true);
      store.commit('showCVV', true);
    }

    OrderService.proccessOrder({
      orderId: orderDetails.order.id,
    }).subscribe(
      async res => {
        if (
          metadata &&
          metadata.expedia &&
          metadata.expedia.paymentSessionId &&
          res?.metadata?.expedia?.encodedChallengeConfig
        ) {
          res.metadata = {
            expedia: {
              paymentSessionId: metadata.expedia.paymentSessionId,
              encodedChallengeConfig:
                res?.metadata?.expedia?.encodedChallengeConfig,
            },
          };
        }

        store.dispatch('completeBooking', res);
      },
      err => {
        store.dispatch('onBookingFailed', err);
      },
    );
  },

  async completeBooking(
    store: ActionContext<State, any>,
    orderDetails: Order.Ml.Response.Booking,
  ) {
    const metadata = orderDetails.metadata;

    if (
      metadata &&
      metadata.expedia &&
      metadata.expedia.encodedChallengeConfig &&
      metadata.expedia.paymentSessionId &&
      metadata.expedia.encodedChallengeConfig
    ) {
      store.commit('showCVV', false);
      store.commit('showEPSIframe', true);

      const challengeEither = await Expedia.challenge({
        paymentSessionId: metadata.expedia.paymentSessionId,
        encodedConfig: metadata.expedia.encodedChallengeConfig,
      });

      if (
        isRight(challengeEither) &&
        challengeEither.value.statusCode !== ThreeDsTypes.SessionStatus.Success
      ) {
        const message = challengeEither.value.message;

        if (message === 'Incorrect user input') {
          store.dispatch('onBookingFailed', {
            header: '2FA error',
            message:
              'Incorrect One-Time Password. Check your mobile device and try again.',
          });
        } else {
          store.dispatch('onBookingFailed', {
            header: '2FA error',
            message: 'Failed to challenge a 2FA',
          });
        }

        return;
      }
    }

    OrderService.completeOrder({
      orderId: orderDetails.order.id,
    }).subscribe(
      res => {
        store.dispatch('onSuccessBooking', res);
      },
      err => {
        console.log(err);

        store.dispatch('onBookingFailed', err);
      },
    );
  },

  async handleServerBookingFlow(
    store: ActionContext<State, any>,
    orderDetails: Order.Ml.Response.Booking,
  ) {
    store.commit('setLoading', true);
    store.commit('showCVV', false);
    store.commit('showLoadingDialog', {
      show: true,
      title: String(i18n.global.t('errors.booking.processingTitle')),
      message: String(i18n.global.t('errors.booking.processingMessage')),
    });
    store.dispatch('fetchOrderStatus', orderDetails.order.id);
  },

  async handleServerBookingFlowWith3DS(
    store: ActionContext<State, any>,
    orderDetails: Order.Ml.Response.Booking,
  ) {
    const metadata = orderDetails.metadata;
    if (
      metadata &&
      metadata.expedia &&
      metadata.expedia.paymentSessionId &&
      metadata.expedia.encodedInitConfig &&
      metadata.expedia.encodedChallengeConfig
    ) {
      const paymentSession: ThreeDsTypes.PaymentSession = {
        paymentSessionId: metadata.expedia.paymentSessionId,
        encodedConfig: metadata.expedia.encodedInitConfig,
      };

      const sessionEither = await Expedia.initSession(paymentSession);

      if (
        isLeft(sessionEither) ||
        (isRight(sessionEither) &&
          sessionEither.value.statusCode !== ThreeDsTypes.SessionStatus.Success)
      ) {
        store.dispatch('onBookingFailed', {
          header: failureKeys.TWO_FA_FAILED,
          message: 'Failed to create a 2FA session',
        });

        return;
      }
    }

    const hasDecta3DS =
      metadata && metadata.expedia && metadata.expedia.decta3DS;
    const hasBooking3DS =
      metadata && metadata.booking && metadata.booking.pci3DS;
    const hasItravex3DS =
      metadata && metadata.itravex && metadata.itravex.decta3DS;

    if (hasDecta3DS || hasBooking3DS || hasItravex3DS) {
      const iframeUrl = () => {
        if (hasDecta3DS) {
          return metadata.expedia.decta3DS;
        }

        if (hasItravex3DS) {
          return metadata.itravex.decta3DS;
        }

        return metadata.booking.pci3DS;
      };

      store.commit('showCVV', false);
      store.commit('setLoading', false);

      store.dispatch('showThreeDSIFrame', iframeUrl());

      const either = await threeDsController.subscribe();

      if (either.value === Failure.Terminated) {
      }

      store.commit('setThreeDSState', {
        show: false,
        loading: false,
        source: null,
      });

      store.commit('setLoading', true);
      store.commit('showCVV', false);
    }

    if (
      metadata &&
      metadata.expedia &&
      metadata.expedia.encodedChallengeConfig &&
      metadata.expedia.paymentSessionId &&
      metadata.expedia.encodedChallengeConfig
    ) {
      store.commit('showCVV', false);
      store.commit('showEPSIframe', true);

      const challengeEither = await Expedia.challenge({
        paymentSessionId: metadata.expedia.paymentSessionId,
        encodedConfig: metadata.expedia.encodedChallengeConfig,
      });

      if (
        isRight(challengeEither) &&
        challengeEither.value.statusCode !== ThreeDsTypes.SessionStatus.Success
      ) {
        const message = challengeEither.value.message;

        if (message === 'Incorrect user input') {
          store.dispatch('onBookingFailed', {
            header: '2FA error',
            message:
              'Incorrect One-Time Password. Check your mobile device and try again.',
          });
        } else {
          store.dispatch('onBookingFailed', {
            header: '2FA error',
            message: 'Failed to challenge a 2FA',
          });
        }
        return;
      }
    }

    store.commit('setLoading', true);
    store.commit('showCVV', false);
    store.commit('showLoadingDialog', {
      show: true,
      title: String(i18n.global.t('errors.booking.processingTitle')),
      message: String(i18n.global.t('errors.booking.processingMessage')),
    });
    store.dispatch('fetchOrderStatus', orderDetails.order.id);
  },

  async fetchOrderStatus(store: ActionContext<State, any>, orderId: string) {
    OrderService.ML.getOrderV2(orderId).subscribe(
      orderDetails => {
        store.dispatch('handleOrderStatusResponse', { orderDetails });
      },
      err => {
        console.log('Error fetching order status:', err);
        store.dispatch('onBookingFailed', err);
      },
    );
  },

  handleOrderStatusResponse(
    store: ActionContext<State, any>,
    {
      orderDetails,
      pollCount = 0,
    }: { orderDetails: Order.Ml.Response.OrderDetails; pollCount?: number },
  ) {
    const status = orderDetails.status;
    const maxPolls = 40;
    switch (status) {
      case Order.Ml.OrderStatus.Created:
      case Order.Ml.OrderStatus.Pending:
        store.commit('setLoading', true);
        store.commit('showLoadingDialog', {
          show: true,
          title: String(i18n.global.t('errors.booking.processingTitle')),
          message: String(i18n.global.t('errors.booking.processingMessage')),
        });

        if (pollCount < maxPolls) {
          setTimeout(() => {
            store.dispatch('fetchOrderStatusWithPollCount', {
              orderId: orderDetails.id,
              pollCount: pollCount + 1,
            });
          }, 3000);
        } else {
          store.commit('showLoadingDialog', { show: false });
          store.dispatch('onBookingFailed', {
            header: 'Booking Timeout',
            message: String(i18n.global.t('errors.booking.timeoutMessage')),
          });
        }
        break;

      case Order.Ml.OrderStatus.Booked:
        store.commit('showLoadingDialog', { show: false });
        const successResponse: Order.Ml.Response.Booking = {
          order: {
            _id: orderDetails._id,
            id: orderDetails.id,
            status: orderDetails.status,
            orderUrl: orderDetails.orderUrl,
          },
          metadata: {},
        };
        store.dispatch('onSuccessBooking', successResponse);
        break;

      case Order.Ml.OrderStatus.Failed:
      case Order.Ml.OrderStatus.Errored:
        store.commit('showLoadingDialog', { show: false });
        store.dispatch('onBookingFailed', {
          header: 'Booking Failed',
          message: 'Unable to complete your booking. Please try again.',
        });
        break;

      default:
        console.log('Unhandled order status:', status);

        if (pollCount < maxPolls) {
          setTimeout(() => {
            store.dispatch('fetchOrderStatusWithPollCount', {
              orderId: orderDetails.id,
              pollCount: pollCount + 1,
            });
          }, 3000);
        }
        break;
    }
  },

  async fetchOrderStatusWithPollCount(
    store: ActionContext<State, any>,
    { orderId, pollCount }: { orderId: string; pollCount: number },
  ) {
    OrderService.ML.getOrderV2(orderId).subscribe(
      orderDetails => {
        store.dispatch('handleOrderStatusResponse', {
          orderDetails,
          pollCount,
        });
      },
      err => {
        console.log('Error fetching order status:', err);
        store.dispatch('onBookingFailed', err);
      },
    );
  },

  onDeferredOrderPayment(
    store: ActionContext<State, any>,
    ref: Order.DeferredOrderPayment,
  ) {
    OrderService.getOrder(ref.orderId).subscribe(
      order => {
        const storedOrderId = Order.PendingOrder.restore();

        if (
          !storedOrderId ||
          (storedOrderId && storedOrderId !== ref.orderId)
        ) {
          store.commit('showPendingOrder', {
            source: ref.source,
            order: order,
          });
        }
      },
      err => {
        console.log(err);
      },
    );
  },

  cancelPendingOrder(store: ActionContext<State, any>) {
    if (!store.state.pendingOrder) {
      return;
    }
    store.commit('showDeferredOrderCancellationStates', {
      show: true,
      loading: true,
    });
    OrderService.ML.cancelOrderV2(
      store.state.pendingOrder.order.order_id,
    ).subscribe(
      res => {
        store.commit('showDeferredOrderCancellationStates', {
          show: true,
          loading: false,
        });
        store.commit('removePendingOrder');
        store.dispatch('order/setOrderStatus', Order.Ml.OrderStatus.Canceled, {
          root: true,
        });
        store.commit('setLoading', false);
        store.commit('setCVV', '');
        store.commit('showCVV', false);
        store.dispatch('hotel/selectOffer', { offer: null }, { root: true });
        store.commit('setOrder', null);
      },
      err => {
        store.commit('showDeferredOrderCancellationStates', {
          show: true,
          loading: false,
          error: err.message,
        });
        console.log(err);
      },
    );
  },

  killEPS(store: ActionContext<State, any>) {
    store.commit('showEPSIframe', false);
    store.commit('setLoading', false);
    Expedia.destroy();
  },

  showThreeDSIFrame(store: ActionContext<State, any>, url: string) {
    if (url.includes('localhost')) {
      url = url.replace('https', 'http');
    }

    store.commit('setThreeDSState', {
      source: url,
      show: true,
      loading: true,
    });

    setTimeout(() => {
      store.commit('setThreeDSState', {
        loading: false,
      });
    }, 5000);
  },

  async refetchHotelOffer(store: ActionContext<State, any>) {
    const currentOffer = store.rootState.hotel
      .selectedOffer as HotelOffers.Offer;

    if (!currentOffer) {
      return;
    }

    const currentOfferKey = OfferUseCase.composeUniqueOfferKey(currentOffer);

    await store.dispatch(
      'hotel/getOffers',
      { withReload: false },
      { root: true },
    );

    const rooms = store.rootGetters['hotel/rooms'] as HotelOffers.Group[];

    const allOffers = rooms.flatMap(room => room.offers);

    const similarOffer = allOffers.find(offer => {
      const offerKey = OfferUseCase.composeUniqueOfferKey(offer);
      return offerKey === currentOfferKey;
    });

    if (!similarOffer) {
      return;
    }

    await store.dispatch(
      'hotel/selectOffer',
      { offer: similarOffer },
      { root: true },
    );
  },

  async onBookingFailed(store: ActionContext<State, any>, err: FPError) {
    store.commit('setLoading', false);
    store.commit('showEPSIframe', false);

    const error = mapError(err);

    const product = Order.OrderProduct.hotel;

    const offer = store.rootState.hotel.selectedOffer as HotelOffers.Offer;

    if (product == Order.OrderProduct.hotel && offer) {
      const hotel = store.rootState.hotel.hotel;
      const criteria = store.rootState.searchCriteria.criteria;

      // Events.Hotel.Booking.finish({
      //   offer,
      //   hotel,
      //   criteria,
      //   fpError: err,
      //   startTime: store.state.bookingStartTime,
      // });
    }

    store.commit('setCVV', '');

    if (error.header === failureKeys.BAD_REQUEST && offer) {
      store.commit('setErrorOnCVVCard', error.message);
      return;
    }

    if (
      (error.header === failureKeys.OFFER_EXPIRED ||
        error.header === failureKeys.SEARCH_RESULT_EXPIRED) &&
      offer
    ) {
      store.commit('showCVV', false);
      store.dispatch(
        'support/setRefreshDialogShow',
        {
          show: true,
          message: err.message,
        },
        {
          root: true,
        },
      );

      return;
    }

    const orderFailureValuesList: string[] = Object.values(orderFailureKeys);

    if (orderFailureValuesList.includes(error.header)) {
      store.commit('setLoading', true);

      await store.dispatch('refetchHotelOffer');
    }

    store.commit('setLoading', false);

    store.dispatch('showFailure', error);
  },

  onSuccessBooking(
    store: ActionContext<State, any>,
    res: Order.Ml.Response.Booking,
  ) {
    try {
      if (store.state.payment.card_type?.toLowerCase() === 'mastercard') {
        SonicSdk.playSonic();
      }
    } catch (e) {
      console.log(e);
    }

    store.commit('showEPSIframe', false);
    const product = Order.OrderProduct.hotel;

    const offer = store.rootState.hotel.selectedOffer as HotelOffers.Offer;

    if (product === Order.OrderProduct.hotel && offer) {
      const hotel = store.rootState.hotel.hotel;
      const criteria = store.rootState.searchCriteria.criteria;
      Events.Hotel.Booking.finish({
        offer,
        hotel,
        criteria,
        response: res,
        startTime: store.state.bookingStartTime,
      });
    }

    store.commit('setOrderId', {
      id: res.order.id,
      product: product,
    });

    if (store.state.pendingOrder) {
      store.commit('removePendingOrder');
    }

    if (res.order.id) {
      Order.PendingOrder.storeOrderId(res.order.id);
    }

    store.commit('setLoading', false);
    store.commit('setCVV', '');
    store.commit('showCVV', false);
    store.commit('showSuccess', true);
    store.dispatch('hotel/selectOffer', { offer: null }, { root: true });
    store.commit('setOrder', null);
  },

  showFailure(store: ActionContext<State, any>, error: FPError) {
    store.commit('setFailure', mapError(error));
  },
  onDestroy(store: ActionContext<State, any>) {
    store.commit('resetStore');
  },
};

export interface Book {
  product: Order.OrderProduct;
  split?: string;
  planId: string;
  guest: User.Guest;
  contacts: User.Contacts;
  credentials: User.Credentials;
  offer: HotelOffers.Offer | Cars.Offer;
  criteria?: HotelSearchCriteria;
}

export const booking = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
